// Reports the top of the payment funnel — a buy dialog shown, the store opened —
// for the admin Payment Funnel chart. Checkout onwards is already recorded by
// the payment endpoints, so only these happen-before-a-transaction steps go here.

import apiClient from 'src/helpers/apiClient';
import type { LogPaymentFunnelEventBody } from 'src/openapi';

// Why a buy dialog opened. `out_of_credits` is the shortfall paywall;
// `locked_feature` asks for Pro with nothing spent; `browse` is the user
// opening it themselves to look at prices.
export type PaymentFunnelTrigger =
  | 'out_of_credits'
  | 'locked_feature'
  | 'browse';

const log = (body: LogPaymentFunnelEventBody) => {
  // Signed-in users only: a guest's 401 would send the auth interceptor
  // through a refresh and a logout for a call nobody is waiting on.
  if (!apiClient.apiKey) return;
  apiClient
    .getPaymentApi()
    .then((api) =>
      api.paymentApiLogPaymentFunnelEvent({
        logPaymentFunnelEventBody: {
          ...body,
          page_path: window.location.pathname,
        },
      }),
    )
    .catch(() => {
      // best-effort: a lost event must never surface to the buyer
    });
};

// What a buy dialog actually put on screen. The trigger says only why it
// opened, and every out-of-credits wall now leads with the Pro pitch.
export type PaymentFunnelPopup =
  | 'pro_pitch'
  | 'ai_packs'
  | 'credit_bags'
  | 'checkout';

/**
 * `reason` is a short slug for what opened it ("tailor", "cover_letter",
 * "course") -- lowercase letters, digits and underscores, at most 32.
 */
export const logBuyDialogShown = (
  wallet: 'credits' | 'ai_credits',
  trigger: PaymentFunnelTrigger,
  popup?: PaymentFunnelPopup,
  reason?: string,
) =>
  log({
    event_type: 'buy_dialog_shown',
    wallet,
    trigger,
    // Ahead of the generated client, which predates both fields.
    ...({ popup, reason } as Record<string, string | undefined>),
  });

export const logStoreViewed = () => log({ event_type: 'store_viewed' });
